import {
  ApolloLink,
  ApolloClient,
  InMemoryCache,
  ApolloProvider as DefaultApolloProvider,
  createHttpLink,
  NormalizedCacheObject,
} from '@apollo/client'
import { useAuth0 } from 'app/Auth'
import { createAuthLink } from 'aws-appsync-auth-link'
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link'
import { createContext, ReactNode, useContext, useMemo } from 'react'

// Environment Variables
const SERVICES_GRAPHQL_URL = process.env.REACT_APP_GRAPHQL_URL!
const ISERVICES_GRAPHQL_URL = process.env.REACT_APP_GRAPHQL_ISERVICES_URL!

const AWS_REGION = process.env.REACT_APP_AWS_REGION ?? 'us-east-1'
type TAuthType = 'OPENID_CONNECT' | 'AMAZON_COGNITO_USER_POOLS'
type TApolloProviderWrapperProps = { children: ReactNode }
type TApolloClientsContext = {
  servicesClient: ApolloClient<NormalizedCacheObject>
  iservicesClient: ApolloClient<NormalizedCacheObject>
}

const useCreateApolloClient = (
  apiUrl: string,
  getAccessToken: () => Promise<string>
) => {
  const auth = {
    type: 'OPENID_CONNECT' as TAuthType,
    jwtToken: async () => {
      const token = await getAccessToken()
      return apiUrl === ISERVICES_GRAPHQL_URL ? `Bearer ${token}` : token
    },
  }

  const httpLink = createHttpLink({ uri: apiUrl })

  const link = ApolloLink.from([
    createAuthLink({ url: apiUrl, region: AWS_REGION, auth }),
    createSubscriptionHandshakeLink(
      { url: apiUrl, region: AWS_REGION, auth },
      httpLink
    ),
  ])

  const client = useMemo(
    () =>
      new ApolloClient({
        link,
        cache: new InMemoryCache(),
        connectToDevTools: true,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  return {
    client,
  }
}

const ApolloClientsContext = createContext(
  ({} as TApolloClientsContext) || undefined
)

const ApolloProvider = ({ children }: TApolloProviderWrapperProps) => {
  const { getAccessToken } = useAuth0()

  const value = {
    servicesClient: useCreateApolloClient(SERVICES_GRAPHQL_URL, getAccessToken)
      .client,
    iservicesClient: useCreateApolloClient(
      ISERVICES_GRAPHQL_URL,
      getAccessToken
    ).client,
  }

  return (
    <ApolloClientsContext.Provider value={value}>
      <DefaultApolloProvider client={value.servicesClient}>
        {children}
      </DefaultApolloProvider>
    </ApolloClientsContext.Provider>
  )
}

const useApolloClients = () => {
  const context = useContext(ApolloClientsContext)
  if (!context) {
    throw new Error('useApolloClients must be used within ApolloProvider')
  }
  return context
}

export { ApolloProvider, useApolloClients }
